import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Grid, SvgIcon, Typography } from '@mui/material';
import { IconSearch } from '@tabler/icons-react';
import { Button, DateTimePicker } from '@atoms';
import { useURLparams } from '@hooks';
import { getDatesFromTheLastMonth, isoStringToDate, toISOStringLocal } from '@utils';
import { zodResolver } from '@hookform/resolvers/zod';
import searchBoxSchema from './SearchBox.schema';
import styles from './styles';

const DATE_PICKER_PROPS = {
  size: 'medium',
  dateOnly: true,
  readOnly: false,
  disablePast: false,
  calendarViews: ['year', 'month', 'day'],
  fullWidth: true,
};

interface props {
  filtersOpen?: boolean;
  setFiltersOpen?: (open: boolean) => void;
  isLoading?: boolean;
  dataLength?: number;
}

/**
 * Displays filters
 * @param {props} props
 * @param {object} props.params
 */
const SearchBox: React.FC<props> = ({ isLoading = false }: props) => {
  const { params, setURLparams } = useURLparams<{
    toDate?: string;
    fromDate?: string;
    customerId?: string;
  }>();
  const { t } = useTranslation();
  const { fromDate, toDate } = getDatesFromTheLastMonth();
  /**
   * @function search
   * @description This function is used to search the list.
   */
  const onSubmit = (_params: { toDate?: Date; fromDate?: Date }) => {
    const newParams: Record<string, string> = {
      ...(params?.customerId && { customerId: params.customerId }),
      ...(_params?.fromDate && { fromDate: toISOStringLocal(_params.fromDate) }),
      ...(_params?.toDate && { toDate: toISOStringLocal(_params.toDate) }),
    };
    setURLparams(newParams);
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      fromDate: params.fromDate ? isoStringToDate(params.fromDate) : fromDate,
      toDate: params.toDate ? isoStringToDate(params.toDate) : toDate,
    },
    resolver: zodResolver(searchBoxSchema),
    mode: 'onChange',
  });
  const disableButton = isLoading || isSubmitting;

  return (
    <Box sx={styles.searchContainer}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('TRANSFER_RECEIPT:TITLE')}</Typography>
      </Box>
      <Box sx={styles.content}>
        <Box sx={styles.firstLine}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Controller
                name='fromDate'
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    handleChange={(date: Date) => field.onChange(date)}
                    error={Boolean(errors.fromDate)}
                    inputLabel='COMMON:FROM_DATE'
                    helperText={errors.fromDate ? String(errors.fromDate?.message) : undefined}
                    {...DATE_PICKER_PROPS}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Controller
                name='toDate'
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    handleChange={(date: Date) => field.onChange(date)}
                    error={Boolean(errors.toDate)}
                    inputLabel='COMMON:TO_DATE'
                    helperText={errors.toDate ? String(errors.toDate?.message) : undefined}
                    {...DATE_PICKER_PROPS}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={styles.actionsContainer}>
          <Button
            customStyle={styles.clearButton}
            text={t('COMMON:CLEAR_FILTER')}
            color='primary'
            variant='link'
            onClick={() => {
              reset({ fromDate, toDate });
              onSubmit({ fromDate, toDate });
            }}
          />
          <Button
            customStyle={styles.searchButton}
            startIcon={
              <SvgIcon>
                <IconSearch />
              </SvgIcon>
            }
            variant='contained'
            color='primary'
            type='button'
            disabled={disableButton}
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SearchBox;
